import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import X from 'lucide-react/dist/esm/icons/x';
import Send from 'lucide-react/dist/esm/icons/send';
import Archive from 'lucide-react/dist/esm/icons/archive';
import ArchiveRestore from 'lucide-react/dist/esm/icons/archive-restore';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Bot from 'lucide-react/dist/esm/icons/bot';
import UserIcon from 'lucide-react/dist/esm/icons/user';
import Star from 'lucide-react/dist/esm/icons/star';
import {
  SlidePanel, Button, IconButton, Badge, Text, Spinner, Tabs, TabList, Tab, TabPanel, Select, TextArea, useToast,
  MentionPicker,
} from 'components/ui';
import type { MentionItem } from 'components/ui';
import { useSalesStore } from '../../stores/salesStore';
import { useUIStore, useOrgStore } from 'stores';
import { getTranslation } from 'i18n';
import { salesApi } from '../../services/salesApi';
import { STATUS_CONFIG, STATUS_DOT_COLOR, KANBAN_COLUMNS } from '../../types/sales';
import type { SalesComment, SalesLeadStatus } from '../../types/sales';

function RatingStars({
  value,
  onChange,
  readonly = false,
  color = 'amber',
}: {
  value: number | null;
  onChange?: (v: number) => void;
  readonly?: boolean;
  color?: 'amber' | 'purple';
}) {
  const filled = color === 'purple' ? 'text-purple-400 fill-purple-400' : 'text-amber-400 fill-amber-400';
  const hoverEmpty = color === 'purple' ? 'hover:text-purple-300' : 'hover:text-amber-300';
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(n)}
          className={`border-none bg-transparent p-0 ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <Star
            size={16}
            className={
              (value ?? 0) >= n
                ? filled
                : `text-content-faint ${!readonly ? hoverEmpty : ''}`
            }
            strokeWidth={1.5}
          />
        </button>
      ))}
    </span>
  );
}

function CommentContent({ content }: { content: string }) {
  const parts = content.split(/(@\w+)/g);
  return (
    <div className="text-xs text-content-secondary leading-relaxed break-words">
      {parts.map((part, i) => {
        if (part.startsWith('@')) {
          const isAgent = part === '@agent';
          return (
            <span
              key={i}
              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[11px] font-medium ${
                isAgent
                  ? 'bg-purple-500/10 text-purple-400'
                  : 'bg-brand/10 text-brand'
              }`}
            >
              {isAgent ? <Bot size={10} /> : <UserIcon size={10} />}
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </div>
  );
}

function AgentCommentContent({ content }: { content: string }) {
  return (
    <div
      className="
        prose prose-sm max-w-none text-xs text-content-secondary leading-relaxed break-words
        [&_p]:text-xs [&_p]:my-1
        [&_ul]:text-xs [&_ul]:my-1 [&_ol]:text-xs [&_ol]:my-1 [&_li]:text-xs [&_li]:my-0
        [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs
        [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold
        [&_h1]:text-content [&_h2]:text-content [&_h3]:text-content [&_h4]:text-content [&_h5]:text-content [&_h6]:text-content
        [&_h1]:my-2 [&_h2]:my-2 [&_h3]:my-1.5 [&_h4]:my-1.5 [&_h5]:my-1 [&_h6]:my-1
        [&_strong]:text-content [&_strong]:text-xs
        [&_em]:text-xs [&_a]:text-xs
        [&_code]:text-[11px]
        [&_pre]:text-[11px] [&_pre]:my-2 [&_pre]:p-2 [&_pre]:rounded-md
        [&_blockquote]:text-xs [&_blockquote]:my-2 [&_blockquote]:pl-3
        [&_table]:text-xs [&_th]:text-xs [&_td]:text-xs
      "
    >
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}

function CommentItem({ comment, agentLabel, userLabel }: { comment: SalesComment; agentLabel: string; userLabel: string }) {
  return (
    <div className={`flex gap-3 py-3 ${comment.is_agent ? 'bg-purple-500/5 -mx-4 px-4 rounded-lg' : ''}`}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          comment.is_agent
            ? 'bg-purple-500/15 text-purple-400'
            : 'bg-surface-secondary text-content-muted'
        }`}
      >
        {comment.is_agent ? <Bot size={14} /> : <UserIcon size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-content">
            {comment.is_agent ? agentLabel : comment.name || comment.username || userLabel}
          </span>
          <span className="text-[10px] text-content-muted">
            {new Date(comment.created_at).toLocaleString()}
          </span>
        </div>
        {comment.is_agent
          ? <AgentCommentContent content={comment.content} />
          : <CommentContent content={comment.content} />}
      </div>
    </div>
  );
}

// ── contentEditable mention utilities ─────────────────────

const AGENT_STYLE = 'display:inline-flex;align-items:center;gap:2px;padding:1px 7px;border-radius:4px;font-size:11px;font-weight:500;background:rgba(168,85,247,0.15);color:rgb(192,132,252);white-space:nowrap';
const USER_STYLE  = 'display:inline-flex;align-items:center;gap:2px;padding:1px 7px;border-radius:4px;font-size:11px;font-weight:500;background:rgba(249,115,22,0.15);color:rgb(251,146,60);white-space:nowrap';

/** Build innerHTML: chip any @word followed by whitespace. */
function buildMentionHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/@(\w+)(?=\s)/g, (_, w) =>
      `<span style="${w === 'agent' ? AGENT_STYLE : USER_STYLE}" contenteditable="false">@${w}</span>`
    );
}

/** Save caret as char offset across all text nodes (including non-editable). */
function saveCaretOffset(el: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return 0;
  const range = sel.getRangeAt(0).cloneRange();
  range.selectNodeContents(el);
  range.setEnd(sel.getRangeAt(0).startContainer, sel.getRangeAt(0).startOffset);
  return range.toString().length;
}

/** Restore caret, skipping into editable text nodes; place after chip if offset lands inside one. */
function restoreCaretOffset(el: HTMLElement, target: number) {
  const sel = window.getSelection();
  if (!sel) return;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let chars = 0;
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const len = node.length;
    if (chars + len >= target) {
      const editable = node.parentElement?.getAttribute('contenteditable') !== 'false';
      const range = document.createRange();
      if (editable) {
        range.setStart(node, target - chars);
      } else {
        range.setStartAfter(node.parentElement!);
      }
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    chars += len;
  }
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Find the active partial mention right before `caretOffset` in `text`.
 * Returns { query, start } where start is the index of the leading "@".
 * Triggers only when "@" sits at start-of-text or after whitespace and is
 * followed by ≥1 word character with no whitespace between them and the caret.
 */
function findActiveMention(text: string, caretOffset: number): { query: string; start: number } | null {
  const before = text.slice(0, caretOffset);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before.charAt(at - 1))) return null;
  const partial = before.slice(at + 1);
  if (!/^\w+$/.test(partial)) return null;
  return { query: partial, start: at };
}

/** Caret rect in viewport coords; returns null if no selection. */
function getCaretViewportRect(): DOMRect | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0).cloneRange();
  const rects = range.getClientRects();
  if (rects.length > 0) return rects[0] as DOMRect;
  // Collapsed range at end of text node may yield 0 rects — fall back to parent rect.
  const node = range.startContainer;
  const el = (node.nodeType === 1 ? node : node.parentElement) as HTMLElement | null;
  return el ? (el.getBoundingClientRect() as DOMRect) : null;
}

const LeadModal: React.FC = () => {
  const {
    modalOpen, modalLeadId, focusComment, closeLeadModal,
    selectedLead, selectedLeadLoading, fetchLead,
    comments, commentsLoading, fetchComments,
    history, historyLoading, fetchHistory,
    updateLead, toggleArchive, createComment,
  } = useSalesStore();

  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agentResponse, setAgentResponse] = useState('');
  const [isAskingAgent, setIsAskingAgent] = useState(false);
  const commentInputRef = useRef<HTMLDivElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const orgUsers = useOrgStore((s) => s.users);
  const fetchOrgUsers = useOrgStore((s) => s.fetchUsers);
  const currentUserEmail = useOrgStore((s) => s.userContext?.email ?? null);

  type MentionState = { query: string; start: number; anchor: { top: number; left: number; lineHeight: number } } | null;
  const [mention, setMention] = useState<MentionState>(null);

  useEffect(() => {
    if (modalLeadId) {
      fetchLead(modalLeadId);
      fetchComments(modalLeadId);
      fetchHistory(modalLeadId);
      fetchOrgUsers();
    }
  }, [modalLeadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focusComment && !commentsLoading) {
      setActiveTab('comments');
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  }, [focusComment, commentsLoading]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments, agentResponse]);

  const handleClose = useCallback(() => {
    closeLeadModal();
    setActiveTab('overview');
    setCommentText('');
    if (commentInputRef.current) commentInputRef.current.innerHTML = '';
    setAgentResponse('');
    setIsAskingAgent(false);
  }, [closeLeadModal]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleSalesRating = async (rating: number) => {
    if (!modalLeadId) return;
    await updateLead(modalLeadId, { sales_rating: rating });
  };

  const handleArchive = async () => {
    if (!modalLeadId) return;
    const wasArchived = selectedLead?.archived;
    await toggleArchive(modalLeadId);
    toast.success(wasArchived ? t('salesLeadModalToastUnarchived') : t('salesLeadModalToastArchived'));
    handleClose();
  };

  const [refreshingAi, setRefreshingAi] = useState(false);
  const handleRefreshAi = async () => {
    if (!modalLeadId) return;
    setRefreshingAi(true);
    try {
      const updated = await salesApi.refreshAiRating(modalLeadId);
      useSalesStore.setState((s) => ({
        selectedLead: s.selectedLead?.id === modalLeadId ? updated : s.selectedLead,
        leads: s.leads.map((l) => (l.id === modalLeadId ? updated : l)),
      }));
      toast.success(t('salesLeadModalToastAiRefreshed'));
    } catch {
      toast.error(t('salesLeadModalErrorRefreshAi'));
    } finally {
      setRefreshingAi(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!modalLeadId || newStatus === lead?.status) return;
    await updateLead(modalLeadId, { status: newStatus });
  };

  const mentionItems = React.useMemo<MentionItem[]>(() => {
    return orgUsers
      .filter((u) => u.status !== 'deactivated')
      .filter((u) => !currentUserEmail || u.email.toLowerCase() !== currentUserEmail.toLowerCase())
      .map((u) => {
        const handle = u.email.split('@')[0].toLowerCase();
        const label = u.display_name || u.email;
        return {
          id: u.id,
          handle,
          label,
          sublabel: u.email,
          icon: <UserIcon size={13} />,
          accentClassName: 'bg-brand/10 text-brand',
        };
      });
  }, [orgUsers, currentUserEmail]);

  const agentMentionItem: MentionItem = React.useMemo(() => ({
    id: '__agent__',
    handle: 'agent',
    label: t('salesLeadModalAgentLabel'),
    sublabel: '@agent',
    icon: <Bot size={13} />,
    accentClassName: 'bg-purple-500/15 text-purple-400',
  }), [t]);

  const closeMention = useCallback(() => setMention(null), []);

  const insertMention = useCallback((handle: string) => {
    const el = commentInputRef.current;
    if (!el || !mention) return;
    const plain = el.textContent || '';
    const before = plain.slice(0, mention.start);
    const afterCaret = plain.slice(mention.start + 1 + mention.query.length);
    const insert = `@${handle} `;
    const next = before + insert + afterCaret;
    const newCaret = (before + insert).length;

    setCommentText(next);
    el.innerHTML = buildMentionHtml(next);
    restoreCaretOffset(el, newCaret);
    setMention(null);
    el.focus();
  }, [mention]);

  const handleSendComment = async () => {
    if (!commentText.trim() || !modalLeadId) return;

    const agentMatch = commentText.match(/@agent\s+([\s\S]*)/);
    if (agentMatch) {
      setIsAskingAgent(true);
      setAgentResponse('');
      const question = agentMatch[1].trim();
      const userMessage = commentText;
      setCommentText('');
      if (commentInputRef.current) commentInputRef.current.innerHTML = '';

      try {
        await createComment(modalLeadId, userMessage);
        await salesApi.askAgent(modalLeadId, question, (text) => {
          setAgentResponse(text);
        });
        await fetchComments(modalLeadId);
      } catch {
        toast.error(t('salesLeadModalErrorAgentFailed'));
      } finally {
        setIsAskingAgent(false);
        setAgentResponse('');
      }
      return;
    }

    setSubmitting(true);
    try {
      await createComment(modalLeadId, commentText.trim());
      setCommentText('');
      if (commentInputRef.current) commentInputRef.current.innerHTML = '';
    } catch {
      toast.error(t('salesLeadModalErrorCommentFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const lead = selectedLead;

  return (
    <SlidePanel isOpen={modalOpen} onClose={handleClose} title="" width="max-w-[560px]">
      {selectedLeadLoading || !lead ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : (
        <div className="flex flex-col h-full px-6 py-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 pb-4 border-b border-subtle">
            <div className="min-w-0 flex-1">
              <Text variant="subheading" className="text-lg">{lead.company_name}</Text>
              {lead.website_url && (
                <a
                  href={lead.website_url.startsWith('http') ? lead.website_url : `https://${lead.website_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand hover:underline"
                >
                  {lead.website_url}
                </a>
              )}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <Select
                  value={lead.status}
                  onChange={handleStatusChange}
                  options={KANBAN_COLUMNS.map((s) => ({ value: s, label: t(STATUS_CONFIG[s].labelKey) }))}
                  size="sm"
                  className="w-36"
                />
                {lead.source === 'ai_generated' && (
                  <Badge variant="info" size="sm">
                    <span className="inline-flex items-center gap-1"><Sparkles size={10} /> {t('salesLeadModalBadgeAiDiscovered')}</span>
                  </Badge>
                )}
                {lead.contact_name && (
                  <span className="text-xs text-content-secondary">{lead.contact_name}</span>
                )}
                {lead.contact_email && (
                  <span className="text-xs text-content-muted">{lead.contact_email}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleArchive}
                leftIcon={lead.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
              >
                {lead.archived ? t('salesLeadModalBtnUnarchive') : t('salesLeadModalBtnArchive')}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <TabList>
              <Tab value="overview">{t('salesLeadModalTabOverview')}</Tab>
              <Tab value="comments">
                {t('salesLeadModalTabComments')}
                {comments.length > 0 && (
                  <Badge variant="neutral" size="sm" className="ml-1.5">{comments.length}</Badge>
                )}
              </Tab>
              <Tab value="history">
                {t('salesLeadModalTabHistory')}
                {history.length > 0 && (
                  <Badge variant="neutral" size="sm" className="ml-1.5">{history.length}</Badge>
                )}
              </Tab>
            </TabList>

            {/* Overview */}
            <TabPanel value="overview">
              <div className="py-4 space-y-5 overflow-y-auto">
                {/* Ratings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Text variant="label" className="block mb-1.5">{t('salesLeadModalLabelAiRating')}</Text>
                    <div className="flex items-center gap-2">
                      <RatingStars value={lead.ai_rating} readonly color="purple" />
                      <button
                        onClick={handleRefreshAi}
                        disabled={refreshingAi}
                        className="p-0.5 rounded hover:bg-surface-secondary transition-colors border-none bg-transparent cursor-pointer text-content-muted hover:text-content-secondary disabled:opacity-50"
                        title={t('salesLeadModalBtnRefreshAi')}
                      >
                        <RefreshCw size={11} className={refreshingAi ? 'animate-spin' : ''} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <Text variant="label" className="block mb-1.5">{t('salesLeadModalLabelYourRating')}</Text>
                    <RatingStars value={lead.sales_rating} onChange={handleSalesRating} color="amber" />
                  </div>
                </div>

                {lead.business_info && (
                  <div>
                    <Text variant="label" className="mb-1.5">{t('salesLeadModalLabelBusinessInfo')}</Text>
                    <p className="text-sm text-content-secondary leading-relaxed">{lead.business_info}</p>
                  </div>
                )}

                {lead.contact_phone && (
                  <div>
                    <Text variant="label" className="mb-1.5">{t('salesLeadModalLabelPhone')}</Text>
                    <a
                      href={`tel:${lead.contact_phone.replace(/\s/g, '')}`}
                      className="text-sm text-content-secondary hover:underline"
                    >
                      {lead.contact_phone}
                    </a>
                  </div>
                )}

                {lead.inquiry && (
                  <div>
                    <Text variant="label" className="mb-1.5">{t('salesLeadModalLabelSuggestedPitch')}</Text>
                    <p className="text-sm text-content-secondary leading-relaxed">{lead.inquiry}</p>
                  </div>
                )}

                {lead.ai_analysis && (
                  <div>
                    <Text variant="label" className="mb-1.5">{t('salesLeadModalLabelAiAnalysis')}</Text>
                    <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10">
                      <p className="text-xs text-content-secondary leading-relaxed whitespace-pre-line m-0">{lead.ai_analysis}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-2 text-xs text-content-muted">
                  <div>{t('salesLeadModalCreated')}{new Date(lead.created_at).toLocaleDateString()}</div>
                  <div>{t('salesLeadModalUpdated')}{new Date(lead.updated_at).toLocaleDateString()}</div>
                </div>
              </div>
            </TabPanel>

            {/* Comments */}
            <TabPanel value="comments">
              <div className="flex flex-col h-[calc(100vh-320px)] min-w-0">
                <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
                  {commentsLoading ? (
                    <div className="flex justify-center py-8"><Spinner /></div>
                  ) : comments.length === 0 ? (
                    <div className="text-sm text-content-muted text-center py-8">{t('salesLeadModalNoComments')}</div>
                  ) : (
                    <div className="divide-y divide-subtle">
                      {comments.map((c) => (
                        <CommentItem key={c.id} comment={c} agentLabel={t('salesLeadModalAgentLabel')} userLabel={t('salesLeadModalFallbackUser')} />
                      ))}
                    </div>
                  )}

                  {isAskingAgent && (
                    <div className="flex gap-3 py-3 bg-purple-500/5 -mx-4 px-4 rounded-lg mt-2">
                      <div className="w-7 h-7 rounded-full bg-purple-500/15 text-purple-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot size={14} />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-medium text-content mb-1 block">{t('salesLeadModalAgentLabel')}</span>
                        {agentResponse ? (
                          <AgentCommentContent content={agentResponse} />
                        ) : (
                          <div className="flex gap-1 py-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div ref={commentsEndRef} />
                </div>

                {/* Comment input */}
                <div className="border-t border-subtle pt-3 mt-2 min-w-0">
                  <div className="flex gap-2 min-w-0">
                    <div
                      ref={commentInputRef}
                      contentEditable={!isAskingAgent && !submitting}
                      suppressContentEditableWarning
                      onInput={() => {
                        const el = commentInputRef.current;
                        if (!el) return;
                        const offset = saveCaretOffset(el);
                        const plain = el.textContent || '';
                        setCommentText(plain);
                        const html = buildMentionHtml(plain);
                        if (el.innerHTML !== html) {
                          el.innerHTML = html;
                          restoreCaretOffset(el, offset);
                        }
                        const active = findActiveMention(plain, offset);
                        if (active && active.query.length >= 2) {
                          const rect = getCaretViewportRect();
                          const lineHeight = rect ? rect.height || 18 : 18;
                          setMention({
                            query: active.query,
                            start: active.start,
                            anchor: rect
                              ? { top: rect.top, left: rect.left, lineHeight }
                              : (() => {
                                  const r = el.getBoundingClientRect();
                                  return { top: r.top, left: r.left, lineHeight: r.height };
                                })(),
                          });
                        } else if (mention) {
                          setMention(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (mention) {
                          // Picker handles ArrowUp/Down/Enter/Tab/Escape via a window listener.
                          // Don't submit on Enter while picker is open.
                          if (['Enter', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendComment();
                        }
                      }}
                      onBlur={() => {
                        // Defer so a click on a picker item can fire mousedown first.
                        setTimeout(() => setMention(null), 100);
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const text = e.clipboardData.getData('text/plain');
                        document.execCommand('insertText', false, text);
                      }}
                      data-placeholder={t('salesLeadModalCommentPlaceholder')}
                      className={`
                        flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-surface-primary border border-subtle text-content text-sm
                        outline-none focus:border-brand/40 transition-all min-h-[42px] max-h-[120px]
                        overflow-y-auto overflow-x-hidden break-words whitespace-pre-wrap
                        empty:before:content-[attr(data-placeholder)] empty:before:text-content-muted empty:before:pointer-events-none
                        ${isAskingAgent || submitting ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    />
                    <button
                      onClick={handleSendComment}
                      disabled={!commentText.trim() || submitting || isAskingAgent}
                      className="px-3 rounded-xl bg-[rgb(var(--brand))] text-content-inverse hover:bg-[rgb(var(--brand-hover))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-none cursor-pointer"
                    >
                      {submitting ? <Spinner size="sm" /> : <Send size={14} />}
                    </button>
                  </div>
                  <p className="text-[10px] text-content-muted mt-1">
                    {t('salesLeadModalCommentHintPrefix')}<code className="px-1 bg-surface-secondary rounded">@agent</code> {t('salesLeadModalCommentHint')}
                  </p>
                </div>
              </div>
            </TabPanel>

            {/* History */}
            <TabPanel value="history">
              <div className="py-4 overflow-y-auto">
                {historyLoading ? (
                  <div className="flex justify-center py-8"><Spinner /></div>
                ) : history.length === 0 ? (
                    <div className="text-sm text-content-muted text-center py-8">{t('salesLeadModalNoHistory')}</div>
                ) : (
                  <div className="space-y-3">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-content-muted mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-sm text-content">
                            {h.old_status ? (
                              <>
                                <span className={STATUS_CONFIG[h.old_status as keyof typeof STATUS_CONFIG]?.color || 'text-content-muted'}>
                                  {STATUS_CONFIG[h.old_status as keyof typeof STATUS_CONFIG]?.label || h.old_status}
                                </span>
                                {' → '}
                                <span className={STATUS_CONFIG[h.new_status as keyof typeof STATUS_CONFIG]?.color || 'text-content'}>
                                  {STATUS_CONFIG[h.new_status as keyof typeof STATUS_CONFIG]?.label || h.new_status}
                                </span>
                              </>
                            ) : (
                              <span className={STATUS_CONFIG[h.new_status as keyof typeof STATUS_CONFIG]?.color || 'text-content'}>
                                {t('salesLeadModalHistoryCreatedAs').replace('{{status}}', STATUS_CONFIG[h.new_status as keyof typeof STATUS_CONFIG] ? t(STATUS_CONFIG[h.new_status as keyof typeof STATUS_CONFIG].labelKey) : h.new_status)}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-content-muted mt-0.5">
                            {h.name || h.username || t('salesLeadModalFallbackSystem')} · {new Date(h.changed_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabPanel>
          </Tabs>
        </div>
      )}

      <MentionPicker
        anchor={mention?.anchor ?? null}
        query={mention?.query ?? ''}
        items={mentionItems}
        pinnedFirst={agentMentionItem}
        onSelect={(item) => insertMention(item.handle)}
        onClose={closeMention}
      />
    </SlidePanel>
  );
};

export default LeadModal;
